import React from 'react';
import Skeleton from 'react-loading-skeleton';
import Fade from 'react-reveal/Fade';
import { useSite } from '../../context/SiteContext';
import heroWb from '../../assets/images/hero_wb.png';

/* ─────────────────────────────────────────
   ICONS
───────────────────────────────────────── */
const IconStar4 = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ color: 'var(--waya-primary)', flexShrink: 0 }}
  >
    <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" />
  </svg>
);

const IconUsers = ({ color, width = 24, height = 24, strokeWidth = 2 }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconZap = ({ color, width = 24, height = 24, strokeWidth = 2 }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const IconShieldCheck = ({
  color,
  width = 24,
  height = 24,
  strokeWidth = 2,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

/* ─────────────────────────────────────────
   FLOATING CARD
───────────────────────────────────────── */
const FloatingCard = ({
  icon,
  label,
  sub,
  color,
  style,
  animDelay = '0s',
}) => (
  <div
    className="hero-float-card"
    style={{
      position: 'absolute',
      background: 'rgba(255,255,255,0.94)',
      backdropFilter: 'blur(14px)',
      borderRadius: '18px',
      padding: '12px 14px',
      boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '170px',
      zIndex: 10,
      border: '1px solid rgba(255,255,255,0.7)',
      animationDelay: animDelay,
      ...style,
    }}
  >
    <div
      style={{
        width: '42px',
        height: '42px',
        borderRadius: '13px',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, {
        width: 20,
        height: 20,
        strokeWidth: 2.2,
        color,
      })}
    </div>

    <div>
      <div
        style={{
          fontSize: '13px',
          fontWeight: '800',
          color: '#111827',
          lineHeight: 1.2,
          marginBottom: '2px',
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: '11px',
          fontWeight: '500',
          color: '#6b7280',
          lineHeight: 1.4,
        }}
      >
        {sub}
      </div>
    </div>
  </div>
);

const BalanceCard = ({ style, animDelay = '0s' }) => (
  <div
    className="hero-float-card balance-card-override"
    style={{
      position: 'absolute',
      background:
        'linear-gradient(135deg, var(--waya-primary) 0%, #ff4400 100%)',
      borderRadius: '18px',
      padding: '14px 16px',
      boxShadow: '0 18px 36px rgba(255, 68, 0, 0.24)',
      zIndex: 10,
      minWidth: '160px',
      animationDelay: animDelay,
      ...style,
    }}
  >
    <div
      style={{
        fontSize: '9px',
        color: 'rgba(255,255,255,0.82)',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}
    >
      Total Balance
    </div>

    <div
      style={{
        fontSize: '18px',
        fontWeight: '800',
        color: '#fff',
        letterSpacing: '-0.03em',
        marginTop: '5px',
      }}
    >
      ₦2,458,300
    </div>

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '8px',
      }}
    >
      <div
        style={{
          padding: '2px 6px',
          borderRadius: '999px',
          background: 'rgba(255,255,255,0.2)',
          fontSize: '9px',
          color: '#fff',
          fontWeight: '700',
        }}
      >
        ↑ 12.4%
      </div>

      <span
        style={{
          fontSize: '9px',
          color: 'rgba(255,255,255,0.82)',
        }}
      >
        this month
      </span>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   TRUST AVATAR URLS — Black African/Nigerian faces
───────────────────────────────────────── */
const TRUST_AVATARS = [
  'https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=100&h=100&fit=crop&crop=face',
  'https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?w=100&h=100&fit=crop&crop=face',
  'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=100&h=100&fit=crop&crop=face',
];

/* ─────────────────────────────────────────
   HERO SECTION
───────────────────────────────────────── */
export function HeroSectionView() {
  const { homeContent, settings, loading } = useSite();

  const openDownload = () =>
    window.dispatchEvent(new CustomEvent('waya-open-download-popup'));

  return (
    <section
      id="hero"
      className="waya-hero-wrapper"
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: '#fff',
        paddingTop: '105px',
        paddingBottom: '0',
        borderBottomLeftRadius: '32px',
        borderBottomRightRadius: '32px',
      }}
    >
      {/* Hero Background Curve */}
      <div
        className="hero-blob-container"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <svg
          viewBox="0 0 1440 800"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100%', display: 'block' }}
        >
          <path
            fill="#FF4400"
            d="M 1120,0 C 980,250 820,550 780,800 L 1440,800 L 1440,0 Z"
          />
        </svg>
      </div>

      {/* Decorative Circles (matching reference) */}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          right: '5%',
          width: '500px',
          height: '500px',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '50%',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '10%',
          right: '-5%',
          width: '700px',
          height: '700px',
          border: '1px solid rgba(255,255,255,0.05)',
          borderRadius: '50%',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />

      <div
        className="hero-inner-row"
        style={{
          width: '100%',
          maxWidth: '1240px',
          margin: '0 auto',
          padding: '0 5%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '60px',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* LEFT */}
        <Fade left>
          <div
            className="hero-left-col"
            style={{
              flex: 1,
              maxWidth: '540px',
            }}
          >
            {/* Badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '7px 14px',
                border: '1px solid var(--waya-primary-light)',
                background: 'var(--waya-primary-light)',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: '700',
                color: 'var(--waya-primary)',
                marginBottom: '20px',
              }}
            >
              <IconStar4 />
              {homeContent?.pill_text || 'Bank for Every Nigerian'}
            </div>

            {/* Heading */}
            <h1
              style={{
                fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                lineHeight: '1.15',
                letterSpacing: '-0.04em',
                fontWeight: '900',
                color: '#101828',
                marginBottom: '18px',
                maxWidth: '560px',
              }}
            >
              {loading ? (
                <Skeleton count={2} />
              ) : (
                <>
                  {homeContent?.hero_headline?.includes(' | ') ? (
                    <>
                      <span style={{ color: 'var(--waya-primary)' }}>
                        {homeContent.hero_headline.split(' | ')[0]}
                      </span>
                      <br />
                      <span style={{ color: '#101828', display: 'block', marginTop: '6px' }}>
                        {homeContent.hero_headline.split(' | ')[1]}
                      </span>
                    </>
                  ) : homeContent?.hero_headline?.includes(' built for ') ? (
                    <>
                      <span style={{ color: 'var(--waya-primary)' }}>
                        {homeContent.hero_headline.split(' built for ')[0]}
                      </span>
                      <br />
                      <span style={{ color: '#101828' }}>
                        built for {homeContent.hero_headline.split(' built for ')[1]}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--waya-primary)' }}>
                      {homeContent?.hero_headline || 'BANKING FOR THE WAY YOU LIVE.'}
                    </span>
                  )}
                </>
              )}
            </h1>

            {/* Subtext */}
            <p
              style={{
                fontSize: '15px',
                lineHeight: '1.75',
                color: '#667085',
                maxWidth: '500px',
                marginBottom: '30px',
                fontWeight: '400',
              }}
            >
              {loading ? (
                <Skeleton count={3} />
              ) : (
                homeContent?.hero_subtext || 'An all-in-one ecosystem that connects microbusiness owners and everyday people through an exclusive business, social media, and banking platform.'
              )}
            </p>

            {/* CTA */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '14px',
                marginBottom: '38px',
              }}
            >
              <button
                onClick={openDownload}
                style={{
                  background: 'var(--waya-primary)',
                  color: '#fff',
                  border: 'none',
                  padding: '13px 24px',
                  borderRadius: '13px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 10px 24px var(--waya-primary-glow)',
                  transition: '0.25s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Create Free Account
              </button>

              <button
                onClick={openDownload}
                style={{
                  background: '#fff',
                  color: '#344054',
                  border: '1px solid #d0d5dd',
                  padding: '13px 24px',
                  borderRadius: '13px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: '0.25s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                }}
              >
                Download App
              </button>
            </div>

            {/* TRUST BAR */}
            <div
              className="trust-bar"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '20px',
                flexWrap: 'wrap',
                borderTop: '1px solid #f1f5f9',
                paddingTop: '24px',
              }}
            >
              {/* Logos */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '22px',
                  flexWrap: 'wrap',
                }}
              >
                {[
                  {
                    src: homeContent?.cbn_logo || settings?.cbn_logo,
                    alt: 'CBN',
                    text: 'Licensed by',
                  },
                  {
                    src: homeContent?.ndic_logo || settings?.ndic_logo,
                    alt: 'NDIC',
                    text: 'Insured by',
                  },
                ].map(({ src, alt, text }) => (
                  <div
                    key={alt}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#667085',
                        fontWeight: '600',
                      }}
                    >
                      {text}
                    </span>

                    <img
                      src={src}
                      alt={alt}
                      style={{
                        height: '26px',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Users — Black African/Nigerian avatars */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <div style={{ display: 'flex' }}>
                  {TRUST_AVATARS.map((src, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid #fff',
                        marginLeft: idx > 0 ? '-10px' : '0',
                        boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
                      }}
                    >
                      <img
                        src={src}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                  ))}
                </div>

                <p
                  style={{
                    fontSize: '13px',
                    color: '#475467',
                    margin: 0,
                    fontWeight: '500',
                  }}
                >
                  Trusted by{' '}
                  <span style={{ fontWeight: '800', color: '#111827' }}>
                    500,000+
                  </span>{' '}
                  Nigerians
                </p>
              </div>
            </div>
          </div>
        </Fade>

        {/* RIGHT */}
        <Fade right delay={150}>
          <div
            className="hero-right-col"
            style={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '520px',
            }}
          >
            {loading ? (
              <Skeleton
                style={{
                  width: '290px',
                  height: '540px',
                  borderRadius: '36px',
                }}
              />
            ) : (
              <>

                {/* Hero Image */}
                <img
                  className="hide-on-mobile"
                  src={heroWb}
                  alt="WayaBank App"
                  style={{
                    width: '140%',
                    maxWidth: '850px',
                    position: 'absolute',
                    right: '-12%',
                    bottom: '-110px',
                    zIndex: 2,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 32px 64px rgba(0,0,0,0.18))',
                  }}
                />

                {/* Floating Cards */}
                <FloatingCard
                  icon={<IconUsers />}
                  label="Social Banking"
                  sub="All in one app"
                  color="#3b82f6"
                  animDelay="0.1s"
                  style={{ top: '10%', right: '3%' }}
                />

                <FloatingCard
                  icon={<IconZap />}
                  label="Collect Payments"
                  sub="Instant & secure"
                  color="#f97316"
                  animDelay="0.2s"
                  style={{ top: '34%', left: '2%' }}
                />

                <BalanceCard
                  animDelay="0.35s"
                  style={{
                    top: '42%',
                    right: '1%',
                    transform: 'translateY(-50%)',
                  }}
                />

                <FloatingCard
                  icon={<IconUsers />}
                  label="1M+ Connections"
                  sub="Growing daily"
                  color="#3b82f6"
                  animDelay="0.28s"
                  style={{ bottom: '24%', left: '3%' }}
                />

                <FloatingCard
                  icon={<IconShieldCheck />}
                  label="Protected Banking"
                  sub="Fast & secure"
                  color="#22c55e"
                  animDelay="0.4s"
                  style={{ bottom: '13%', right: '-8%' }}
                />
              </>
            )}
          </div>
        </Fade>
      </div>

      {/* RESPONSIVE */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes wayaBlobPulse {
              0% { transform: scale(1) translate(0, 0); }
              50% { transform: scale(1.1) translate(10px, -10px); }
              100% { transform: scale(0.9) translate(-10px, 10px); }
            }

            @keyframes heroCardFloat {
              0%,100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }

            @keyframes heroCardFloatMid {
              0%,100% { transform: translateY(-50%); }
              50% { transform: translateY(calc(-50% - 10px)); }
            }

            .hero-float-card {
              animation: heroCardFloat 4s ease-in-out infinite;
            }

            .balance-card-override {
              animation: heroCardFloatMid 4.2s ease-in-out infinite !important;
            }

            @media (max-width: 1100px) {
              .hero-inner-row { gap: 40px !important; }
              .hero-float-card { transform: scale(0.9); }
            }

            @media (max-width: 991px) {
              .waya-hero-wrapper {
                padding-top: 120px !important;
                padding-bottom: 60px !important;
                background: #fff !important;
              }
              .hero-blob-container {
                display: none !important;
              }
              .hero-inner-row {
                flex-direction: column !important;
                text-align: center;
                gap: 0 !important;
                padding: 0 5% !important;
              }
              .hero-left-col {
                width: 100% !important;
                max-width: 100% !important;
                padding: 0 !important;
                background: transparent !important;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .hero-right-col {
                display: none !important;
              }
              .trust-bar { justify-content: center !important; }
            }

            @media (max-width: 768px) {
              .hero-float-card { display: none !important; }
              .hero-left-col h1 { font-size: 1.8rem !important; }
              .hero-left-col p { font-size: 14px !important; }
              .hero-right-col img { max-width: 320px !important; }
            }

            @media (max-width: 576px) {
              .hero-left-col h1 {
                font-size: 1.6rem !important;
                line-height: 1.25 !important;
              }
              .hero-left-col p {
                font-size: 14px !important;
                line-height: 1.7 !important;
              }
              .hero-right-col img { max-width: 280px !important; }
              .trust-bar {
                flex-direction: column;
                align-items: center !important;
              }
            }
          `,
        }}
      />
    </section>
  );
}
